import * as React from 'react';
import { __ } from 'react-i18n';
import { FlexCol, FlexRowCenter } from '../../Defaults/Flexbox/Flexbox';
import styled from 'styled-components';
import { rem } from 'polished';
import { getCartSummaryInfo } from '../../../utilities/selectors';
import { formatPrice, formatPriceToString, prop } from '../../../utilities';
import { DsiSummaryValue as SummaryValue } from './Summary/SummaryValue';
import { DsiInfoText, DsiInfoType, ZemplinLoaderWrapper } from '../../..';
import { FinalValue } from './Summary/FinalValue';
import { AcceptTermsCheckbox } from './Summary/AcceptTermsCheckbox';
import { Loader } from '../../Zemplin/Cart/Summary/ThirdStep';
import { Coupon } from './Summary/Coupon';
import { Button } from '../General/Button';
import { InfoText, InfoType } from '../General/InfoText';
import { DsiCheckBox } from '../General/Checkbox';

interface Props {
  data: any;
  handleAcceptTermsChange: (e: any) => void;
  handleHeurekaTermsChange: (e: any) => void;
  isFetching: boolean;
  isCreatingOrder?: boolean;
  user: any;
  handleCompleteOrder: () => void;
  handleGoBack: () => void;
  couponCode: string;
  onCouponCodeChanged: any;
  addCoupon: any;
  removeCoupon: any;
  state: any;
  dispatchState: any;
  couponError?: string | null;
  lang: string;
}

function CartSummary({
  data,
  handleAcceptTermsChange,
  handleHeurekaTermsChange,
  isFetching,
  isCreatingOrder,
  user,
  handleCompleteOrder,
  handleGoBack,
  couponCode,
  onCouponCodeChanged,
  addCoupon,
  removeCoupon,
  state,
  dispatchState,
  couponError,
  lang,
}: Props) {
  const {
    totalPrice,
    totalPriceWithoutVat,
    vatTotal,
    currency,
    deliveryPrice,
    paymentPrice,
    totalItemsPriceWithoutVat,
    totalItemsPrice,
    totalItemsPriceWithoutVatBeforeGiftcards,
    totalItemsPriceBeforeGiftcards,
    vat_groups,
    giftCardDiscount,
    nominalGiftCards,
  } = getCartSummaryInfo(data, user);
  const termsAccept = prop(state, 'terms_accept');
  const heureka = prop(state, 'heureka');
  const heurekaRefuse = prop(state, 'heureka_refuse');
  const newsletterAccept = prop(state, 'newsletter_accept');
  const cartProblems = prop(data, 'problems', []);
  const isB2B = user && user.b2b;

  const handleFieldChange = (e: any) => {
    const value = e.target.value;
    const field = e.target.name;
    dispatchState({ field, value });
  };

  let productsPriceText = isB2B
    ? __('Cena tovaru bez DPH')
    : __('Cena tovaru s DPH');
  let summaryPrice = isB2B
    ? formatPrice(totalItemsPriceWithoutVat, currency, 2, false, '', false, 2)
    : formatPrice(
        totalItemsPrice,
        currency,
        2,
        false,
        '',
        false,
        lang === 'sk' ? 2 : 0,
      );
  if (
    nominalGiftCards?.length ||
    giftCardDiscount?.price > 0 ||
    data.discount > 0
  ) {
    productsPriceText = isB2B
      ? __('Pôvodná cena bez DPH')
      : __('Pôvodná cena s DPH');
    summaryPrice = isB2B
      ? formatPrice(
          totalItemsPriceWithoutVatBeforeGiftcards,
          currency,
          2,
          false,
          '',
          false,
          2,
        )
      : formatPrice(
          totalItemsPriceBeforeGiftcards,
          currency,
          2,
          false,
          '',
          false,
          lang === 'sk' ? 2 : 0,
        );
  }

  return (
    <>
      <Wrapper>
        <DesktopFields>
          {data ? (
            <>
              <DesktopWrapper>
                <Coupon
                  code={couponCode}
                  onCodeChanged={onCouponCodeChanged}
                  addCode={addCoupon}
                  removeCode={removeCoupon}
                  activeCoupon={prop(data, 'giftcards.0')}
                  isFetching={isFetching}
                  couponError={couponError}
                  itemsRestrictions={data.items_giftcard_restrictions}
                />
              </DesktopWrapper>

              <SummaryValue
                borderBottom={
                  !nominalGiftCards.length &&
                  giftCardDiscount?.price <= 0 &&
                  data.discount <= 0
                }
                title={productsPriceText}
                value={summaryPrice}
              />

              {nominalGiftCards && nominalGiftCards.length ? (
                nominalGiftCards.map(nominalGiftCard => {
                  return (
                    <SummaryValue
                      key={nominalGiftCard.id}
                      borderBottom={
                        giftCardDiscount?.price <= 0 && data.discount <= 0
                      }
                      title={__('Darčeková poukážka')}
                      value={formatPrice(
                        0 - nominalGiftCard.nominal_price,
                        currency,
                      )}
                    />
                  );
                })
              ) : (
                <></>
              )}

              {giftCardDiscount?.price > 0 && (
                <SummaryValue
                  borderBottom={data.discount <= 0}
                  title={isB2B ? __('Zľavový kupón') : __('Zľavový kupón')}
                  value={
                    isB2B
                      ? formatPrice(
                          0 - giftCardDiscount?.priceWithoutVat,
                          currency,
                        )
                      : formatPrice(0 - giftCardDiscount?.price, currency)
                  }
                />
              )}

              {prop(data, 'delivery.name', '') ? (
                deliveryPrice || deliveryPrice === 0 ? (
                  <SummaryValue
                    borderBottom={paymentPrice <= 0}
                    title={
                      prop(data, 'delivery.translations.0.delivery_name') ||
                      prop(data, 'delivery.name', '')
                    }
                    value={formatPrice(
                      deliveryPrice,
                      currency,
                      2,
                      true,
                      '',
                      false,
                      lang === 'cz' && !isB2B ? 0 : 2,
                    )}
                  />
                ) : null
              ) : null}
              {prop(data, 'payment.name', '') ? (
                paymentPrice || paymentPrice === 0 ? (
                  <SummaryValue
                    borderBottom={true}
                    title={
                      prop(data, 'payment.translations.0.payment_name') ||
                      prop(data, 'payment.name', '')
                    }
                    value={formatPrice(
                      paymentPrice,
                      currency,
                      2,
                      true,
                      '',
                      false,
                      lang === 'cz' && !isB2B ? 0 : 2,
                    )}
                  />
                ) : null
              ) : null}

              {data.discount > 0 ? (
                <SummaryValue
                  borderBottom={true}
                  title={__('Zľava')}
                  value={`-${formatPrice(
                    data.discount,
                    currency,
                    2,
                    true,
                    '',
                    false,
                    lang === 'cz' && !isB2B ? 0 : 2,
                  )}`}
                />
              ) : null}

              {vat_groups &&
                Object.keys(vat_groups).map(key => {
                  const vatGroup = vat_groups[key];
                  /*
                  if (vatGroup.price_without_vat <= 0) {
                    return null;
                  }
                  */
                  return (
                    <>
                      <SummaryValue
                        title={`${__('Základ dane pre')} ${vatGroup.vat_rate}%`}
                        value={formatPrice(
                          vatGroup.show_price_without_vat,
                          currency,
                          2,
                          false,
                          '',
                          false,
                          lang === 'cz' && !isB2B ? 0 : 2,
                        )}
                      />
                      <SummaryValue
                        title={`${__('DPH')} ${vatGroup.vat_rate}%`}
                        value={formatPrice(
                          vatGroup.vat_price,
                          currency,
                          2,
                          false,
                          '',
                          false,
                          lang === 'cz' && !isB2B ? 0 : 2,
                        )}
                      />
                      <SummaryValue
                        borderBottom={true}
                        title={__('Cena s DPH')}
                        value={formatPrice(
                          vatGroup.price,
                          currency,
                          2,
                          false,
                          '',
                          false,
                          lang === 'cz' && !isB2B ? 0 : 2,
                        )}
                      />
                    </>
                  );
                })}

              <FinalValue
                title={__('Celkom k úhrade s DPH')}
                value={formatPrice(
                  totalPrice,
                  currency,
                  2,
                  false,
                  '',
                  false,
                  lang === 'cz' && !isB2B ? 0 : 2,
                )}
                borderBottom={true}
              />
              {!isB2B && (
                <DsiCheckBox
                  name="heureka_refuse"
                  onClick={handleFieldChange}
                  selected={heurekaRefuse}
                  label={__(
                    'Nesúhlasím so zaslaním dotazníka spokojnosti v rámci programu Overené zákazníkmi, ktorý pomáha zlepšovať vaše služby.',
                  )}
                  wrapperStyle={{ marginBottom: rem(16) }}
                />
              )}

              {(!user || (user && !user?.newsletter_accept && !user.b2b)) && (
                <DsiCheckBox
                  name="newsletter_accept"
                  onClick={handleFieldChange}
                  selected={newsletterAccept}
                  label={__('Súhlasím so začlenením do newslettera')}
                  wrapperStyle={{ marginBottom: rem(16) }}
                />
              )}

              <AcceptTermsCheckbox
                name="terms_accept"
                onClick={handleFieldChange}
                selected={termsAccept}
              />
              {(prop(data, 'payment.id', '') || '')
                .toUpperCase()
                .includes('DOBIERK') &&
                lang === 'sk' && (
                  <DsiInfoText
                    type={DsiInfoType.INFO}
                    info={__(
                      'Od 1.7 vstupuje do platnosti nový zákon o zaokrúhľovaní centov pri platbe v hotovosti. Výsledná suma pri platbe na dobierku v hotovosti bude zaokrúhlená na 0 alebo 5 centov.',
                    )}
                    marginBottom={16}
                  />
                )}
              {cartProblems && cartProblems.length > 0 ? (
                <>
                  {cartProblems.map((problem, i) => (
                    <>
                      {i === 0 ? (
                        <>
                          {problem.readable ? (
                            <InfoText
                              key={problem.readable}
                              maxWidth={1168}
                              type={InfoType.ERROR}
                              info={problem.readable}
                              marginBottom={24}
                            />
                          ) : (
                            <InfoText
                              key={'2'}
                              maxWidth={1168}
                              type={InfoType.ERROR}
                              info={__('Vyplňte prosím povinné údaje.')}
                              marginBottom={24}
                            />
                          )}
                        </>
                      ) : null}
                    </>
                  ))}
                </>
              ) : null}
              <NextButton
                disabled={isFetching || isCreatingOrder}
                // tslint:disable-next-line:jsx-no-lambda
                onClick={handleCompleteOrder}
              >
                {isFetching ? (
                  <FlexRowCenter>
                    <Loader className="marginLeft" small={true} />
                  </FlexRowCenter>
                ) : (
                  __('Objednávka s povinnosťou platby')
                )}
              </NextButton>
              <BackButton
                // tslint:disable-next-line:jsx-no-lambda
                onClick={handleGoBack}
              >
                {__('Späť')}
              </BackButton>
            </>
          ) : (
            <ZemplinLoaderWrapper />
          )}
        </DesktopFields>
      </Wrapper>
    </>
  );
}

const Wrapper = styled(FlexCol)`
  ${({ theme }) => theme.mediab.l925`
    margin: 0 auto;
  `}
`;

const DesktopFields = styled(FlexCol)``;

const NextButton = styled(Button)`
  margin: ${rem(15)} 0 0;
  padding: ${rem(8)};
  width: 100%;
  background-color: ${({ theme }) => theme.cart.cartSummary.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${rem(16)};
  font-weight: 500;
  border-radius: ${rem(4)};
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  margin: 0 0 ${rem(16)};

  ${({ theme }) => theme.mediab.m810`
    font-size: ${rem(14)};
  `}

  &:disabled {
    background-color: #f9f9f9;
    color: #cccccc;
  }
`;

const BackButton = styled(NextButton)`
  background-color: transparent;
  border: ${({ theme }) => theme.borders.primary};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-top: 0;
`;

const DesktopWrapper = styled.div`
  display: block;
  margin-bottom: ${rem(32)};

  ${({ theme }) => theme.mediab.m580`
    display: none;
  `}
`;

export { CartSummary as DsiCartSummary };
